import React, { useEffect, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import Card from 'components/card';
import useSWR from 'swr';
import Pagination from 'components/Pagination/Pagination';
import { TPendingVerification } from 'views/types/pendingVerification';
import { useVerificationStore } from 'views/store/useVerificationStore';
import { verificationService } from 'views/services/kycService';
import * as Dialog from '@radix-ui/react-dialog';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { toast } from 'App';

const columnHelper = createColumnHelper<TPendingVerification>();

const columns = [
  columnHelper.accessor('id', {
    header: 'Verification ID',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('user.coins_fly_id', {
    header: 'Client ID',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('user.telegram_name', {
    header: 'Telegram Name',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('created_at', {
    header: 'Created At',
    cell: info =>
      info.getValue()
        ? dayjs(info.getValue()).format('YYYY-MM-DD HH:mm')
        : '---',
  }),
];

export const VerificationsTable = () => {
  const [selectedItem, setSelectedItem] = useState<TPendingVerification | null>(
    null
  );
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { kysItem } = useVerificationStore();
  const { canUpdate, canRead } = usePermissions();

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setPage(0);
  };

  const { data } = useSWR(
    `/api/kyc?page=${page + 1}&limit=${itemsPerPage}`,
    () =>
      verificationService.getVerificationItems({
        page: page + 1,
        limit: itemsPerPage,
      }),
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (selectedItem && !canUpdate(PERMISSION_RESOURCES.PENDING_VERIFICATION)) {
      setSelectedItem(null);
    }
  }, [canUpdate, selectedItem]);

  const onApproveVerification = async () => {
    if (!selectedItem) return;

    if (!canUpdate(PERMISSION_RESOURCES.CONFIRM_CANCEL_CTA)) {
      toast({
        title: 'Access Denied',
        description:
          'You do not have permission to confirm or cancel verifications',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const response = await verificationService.approveVerification(
      selectedItem.id
    );

    if (response) {
      setSelectedItem(null);
    }
  };

  const table = useReactTable({
    data: kysItem,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    verificationService.getVerificationItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-8">
      <Card>
        <div className="p-4">
          <table className="min-w-full border-collapse">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="border-b px-4 py-2 text-left font-medium"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className={`${
                    canUpdate(PERMISSION_RESOURCES.PENDING_VERIFICATION)
                      ? 'hover:bg-gray-50 dark:hover:bg-[black]/20 cursor-pointer'
                      : 'opacity-60 cursor-not-allowed'
                  }`}
                  onClick={() => {
                    if (canUpdate(PERMISSION_RESOURCES.PENDING_VERIFICATION)) {
                      setSelectedItem(row.original);
                    }
                  }}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="border-b px-4 py-2">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {data?.meta?.totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={data?.meta?.totalPages}
              totalItems={data?.meta?.totalItems}
              onPageChange={setPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              totalName="Total Items"
            />
          )}
        </div>
      </Card>

      {selectedItem && (
        <Dialog.Root
          open={!!selectedItem}
          onOpenChange={() => setSelectedItem(null)}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm" />
            <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-full h-[80vh] max-w-[80vw] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white dark:bg-navy-800 dark:text-white p-6 shadow-xl focus:outline-none">
              <Dialog.Title className="text-lg font-bold mb-4">
                Verify
              </Dialog.Title>
              <p className="mb-4">
                {'Are you sure you want to approve verification for ' +
                  selectedItem.user.telegram_name +
                  '?'}
              </p>
              {canRead(PERMISSION_RESOURCES.CONFIRM_CANCEL_CTA) && (
                <div className="flex justify-end gap-2">
                  <Dialog.Close asChild>
                    <button
                      disabled={
                        !canUpdate(PERMISSION_RESOURCES.PENDING_VERIFICATION) ||
                        !canUpdate(PERMISSION_RESOURCES.CONFIRM_CANCEL_CTA)
                      }
                      className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </Dialog.Close>
                  <button
                    disabled={
                      !canUpdate(PERMISSION_RESOURCES.PENDING_VERIFICATION) ||
                      !canUpdate(PERMISSION_RESOURCES.CONFIRM_CANCEL_CTA)
                    }
                    onClick={onApproveVerification}
                    className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Confirm
                  </button>
                </div>
              )}
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
};
